import React, { useRef, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ModalContext } from '$contexts/modal.jsx';
import { useCursorContext } from '$contexts/cursor.jsx';
import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon, CloseIcon } from '$partials/icon';
import { Button, Select } from '$partials/ui';

import Util from '$utilities';
import { formatPageTitle, formatPageUri } from '$utilities/format';

/* ========================================================================= */
/* Configuration */
/* ========================================================================= */

const FORM_ID = '5140ffe1-f8b3-4678-ae7b-39c32b399d46';
const propertyOptions = ['Mid Terrace House', 'End Terrace House', 'Semi Detached House', 'Detached House', 'Converted Flat', 'Purpose Build Flat'];

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modals.d.ts').BookValuationModal}
 */
const BookValuationModal = ({
    context,
    hubspot = {},
    page
}) => {
    const { handleModal } = useContext(ModalContext);
    const { dispatch: cursorDispatch } = useCursorContext();
    const exitButtonRef = useRef(null);

    // Form
    const textareaRef = useRef(null);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ currentStep, setCurrentStep ] = useState(1);
    const [ checkedValues, setCheckedValues ] = useState({
        selling: false,
        letting: false,
    });
    const [ bedrooms, setBedrooms ] = useState(1);
    const [ propertyType, setPropertyType ] = useState('Detached House');
    const [ height, setHeight ] = useState('');
    const [ message, setMessage ] = useState('');
    const [ hasSubmitted, setHasSubmitted ] = useState(false);
    const {
        formState: { errors },
        handleSubmit,
        register,
        reset,
        setError,
        clearErrors,
        getValues
    } = useForm();
    const { ref, ...rest } = register('address');
    const { ref: evaluationRef, ...evalutationRest } = register('evaluation_address');

    useEffect(() => {
        const exit = cursorDispatch({
            type: 'registerArea',
            data: {
                ref: exitButtonRef,
                cursorType: 'exit'
            },
        });

        return () => [exit].map(cleanup => {
            cleanup();});
    }, []);

    const submit = async (data) => {
        if (!FORM_ID) {
            return;
        }
        const sellingResons = Object.keys(checkedValues).filter(value => checkedValues[value]);
        const formFields = [
            {
                objectTypeId: '0-1',
                name: 'email',
                value : data?.email
            },
            {
                objectTypeId: '0-1',
                name: 'firstname',
                value : data?.firstname
            },
            {
                objectTypeId: '0-1',
                name: 'lastname',
                value : data?.lastname
            },
            {
                objectTypeId: '0-1',
                name: 'phone',
                value : data?.phone
            },
            {
                objectTypeId: '2-107254668',
                name: 'evaluation_address',
                value : data?.evaluation_address
            },
            {
                objectTypeId: '2-107254668',
                name: 'property_type',
                value : propertyType
            },
            {
                objectTypeId: '2-107254668',
                name: 'selling_reason',
                value : sellingResons.join(', ')
            },
            {
                objectTypeId: '2-107254668',
                name: 'bedrooms_min',
                value : bedrooms
            },
            {
                objectTypeId: '2-107254668',
                name: 'enquiry_name',
                value : `Valuation enquiry — ${data?.firstname} ${data.lastname}`
            },
            {
                objectTypeId: '2-107254668',
                name: 'form_context',
                value: context
            }
        ];

        if (data.address) {
            formFields.push({
                objectTypeId: '0-1',
                name: 'address',
                value : data?.address
            });
        }

        try {
            const response = await fetch('/api/crm/v1/form', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': Util.cookies.getItem('crumb')
                },
                body: JSON.stringify({
                    formId: FORM_ID,
                    fields: formFields,
                    context:  {
                        hutk: window.sessionStorage.getItem('hubspot_hubspotutk') || undefined,
                        pageUri: formatPageUri(),
                        pageName: formatPageTitle(page)
                    }
                })
            });

            const result = await response.json();

            if (response.status !== 200) {
                if (result && result.message) {
                    setMessage(result.message || 'Thank you for your booking');
                    setHasSubmitted(true);
                    return;
                } else {
                    throw new Error(response.status);
                }
            }
            setHasSubmitted(true);
            setMessage(result?.message || 'Thank you for your booking');
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setMessage('Sorry, something went wrong, please try again later.');
        }
    };

    const handleInputHeight = () => {
        if (textareaRef.current){
            setHeight(textareaRef.current.scrollHeight);
        }
    };

    return (
        <>
            <div
                className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-black mix-blend-multiply z3 cursor-none'}
                onClick={() => {
                    handleModal();
                    reset();
                }}
                ref={exitButtonRef}
                style={{opacity: '0.2'}}>
            </div>
            <div
                className='height-100 bg-white z4 fixed top-0 overflow-auto modal-container'>
                <button
                    aria-label='Close'
                    className='form-field-reset link-reset absolute right-0 right mt6 mr6 cursor-pointer'
                    onClick={() => {
                        handleModal();
                        reset();
                    }}
                    type='button'>
                    <CloseIcon/>
                </button>
                <div className='py7 px5 md-px6'>

                    <div
                        className='rich-text type-style-2'
                        dangerouslySetInnerHTML={{ __html: hasSubmitted ? message : 'Book A Valuation'}} />
                    <p className='pt5 pb7 type-style-6'>
                        {hasSubmitted ? '' : 'An expert from our local branch will provide you with the most accurate valuation.'}
                    </p>

                    {hasSubmitted ? (
                        <Button
                            aria-label='Submit'
                            as='button'
                            className = 'bg-black white type-style-5 items-center'
                            look = 'fill'
                            onClick={() => {
                                handleModal();
                                reset();
                            }}
                            renderIcon={<ArrowRightIcon />}>
                                Close
                        </Button>
                    ) : (
                        <form
                            autoComplete='off'
                            data-analytics='book-valuation'
                            data-form-id={FORM_ID}
                            data-portal-id={hubspot.portalId}
                            onSubmit={handleSubmit(submit)}>

                            {/* Steps */}
                            <div className='col-12 flex mb7'>
                                <button
                                    className={`form-field-reset align-left col-6 mr1 pb3 border-bottom cursor-pointer black${currentStep === 1 ? '' : ' opacity-0-4'}`}
                                    onClick={() => currentStep === 2 && setCurrentStep(1)}
                                    type='button'>
                                        Step 01
                                </button>

                                <button
                                    className={`form-field-reset align-left col-6 ml1 pb3 border-bottom cursor-pointer black${currentStep === 2 ? '' : ' opacity-0-4'}`}
                                    onClick={() => {
                                        if (currentStep === 1 && (!getValues('evaluation_address') || (!checkedValues.selling && !checkedValues.letting))) {
                                            !getValues('evaluation_address') && setError('evaluation_address', { type: 'required', name: 'evaluation_address',
                                                message: 'Please enter your property address' });
                                            (!checkedValues.selling && !checkedValues.letting) && setError('selling_reason', { type: 'required',
                                                message: 'Please select a selling reason' });
                                        } else if (currentStep === 1) {
                                            setCurrentStep(2);
                                        }
                                    }}
                                    type='button'>
                                        Step 02
                                </button>
                            </div>

                            {/*  Step 01 fields */}
                            {currentStep === 1 && (
                                <>
                                    {/*  Selling reason checkbox */}
                                    <div className='flex checkbox items-center mb3 relative'>
                                        <input
                                            checked={checkedValues.selling}
                                            id='selling'
                                            name='selling'
                                            onChange={(e) => {
                                                if(e.target.checked){
                                                    clearErrors('selling_reason');
                                                }
                                                setCheckedValues(prev => ({...checkedValues, selling: !prev.selling}));
                                            }}
                                            type='checkbox'
                                            value='selling'/>
                                        <label
                                            className='type-style-6'
                                            htmlFor='selling'>Sell</label>
                                    </div>
                                    <div className='flex checkbox items-center relative'>
                                        <input
                                            checked={checkedValues.letting}
                                            id='letting'
                                            name='letting'
                                            onChange={(e) => {
                                                if(e.target.checked){
                                                    clearErrors('selling_reason');
                                                }
                                                setCheckedValues(prev => ({...checkedValues, letting: !prev.letting}));}
                                            }
                                            type='checkbox'
                                            value='letting'/>
                                        <label
                                            className='type-style-6'
                                            htmlFor='letting'>Let</label>
                                    </div>
                                    {errors?.['selling_reason']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['selling_reason']?.message}
                                        </p>
                                    )}

                                    {/* Valuation property address */}
                                    <div className='flex flex-wrap mxn5 mb7 mt7'>
                                        <div className='col-12 px5 '>
                                            <div className='flex width-100 items-center relative'>
                                                <span className='absolute pl5 md-pl6 pb7 md-pb8 top-0 pt2 mt5 pointer-events-none'>
                                                Address
                                                </span>
                                                <textarea
                                                    {...evalutationRest}
                                                    className={`border-top border-left border-right width-100 type-style-3 pt7 pb6 md-pt8 md-pb7 pr6 pl5 md-pl6${errors?.['evaluation_address']?.message ? ' border-bottom red' : ' black'}`}
                                                    id='frm-address'
                                                    name='evaluation_address'
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            clearErrors('evaluation_address');
                                                        }
                                                        handleInputHeight();
                                                    }}
                                                    ref={(e) => {
                                                        evaluationRef(e);
                                                        textareaRef.current = e;
                                                    }}
                                                    rows={1}
                                                    style={{ height: height ? height + 'px' : 'auto'}}/>
                                            </div>
                                            {errors?.['evaluation_address']?.message && (
                                                <p className='my5 red width-100'>
                                                    {errors?.['evaluation_address']?.message}
                                                </p>
                                            )}
                                        </div>

                                        {/* Bedrooms */}
                                        <div className='col-12 px5 '>
                                            <div className='flex width-100 items-center border-top border-left border-right '>
                                                <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                                Bedrooms
                                                </span>
                                                <input
                                                    className={`width-100 sm-pr3 pt7 pb6 md-pt8 md-pb7 type-style-3 pl5 md-pl6 border-bottom${errors?.['address']?.message ? ' border red' : ' black'}`}
                                                    id='frm-bedroom'
                                                    min={1}
                                                    name='bedrooms'
                                                    onInput={(event) => setBedrooms(parseInt(event.target.value))}
                                                    type='number'
                                                    value={bedrooms}/>
                                                <div className='absolute right right-0 mr5 md-mr6 '>
                                                    <div className='flex flex-col mr5 sm-mr6'>
                                                        <button
                                                            aria-label='Increase number of bedrooms'
                                                            className='form-field-reset cursor-pointer'
                                                            onClick={()=> setBedrooms(prev => prev + 1)}
                                                            type='button'>
                                                            <ArrowUpIcon
                                                                className='mb3 black'/>
                                                        </button>
                                                        <button
                                                            aria-label='Decrease number of bedrooms'
                                                            className='form-field-reset cursor-pointer'
                                                            onClick={() => {
                                                                if (bedrooms > 1) {
                                                                    setBedrooms(prev => prev - 1);
                                                                }
                                                            }}
                                                            type='button'>
                                                            <ArrowDownIcon
                                                                className='mt3 black'/>
                                                        </button>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>

                                        {/*  Property type */}
                                        <div className='col-12 px5'>
                                            <Select
                                                className='border-left border-right border-bottom'
                                                id='frm-property-type'
                                                label='Property Type'
                                                name='property_type'
                                                onChange={(value) => {
                                                    setPropertyType(value);
                                                }}
                                                options={propertyOptions.map(option => ({label: option, value: option}))}
                                                value={propertyType}/>
                                        </div>
                                    </div>

                                    {/* Proceed to step 02 */}
                                    <div>
                                        <Button
                                            aria-label='Submit'
                                            as='button'
                                            className = 'bg-black white type-style-5'
                                            disabled={isLoading}
                                            look = 'fill'
                                            onClick={() =>{
                                                if (!getValues('evaluation_address') || (!checkedValues.selling && !checkedValues.letting)) {
                                                    !getValues('evaluation_address') && setError('evaluation_address', { type: 'required', name: 'evaluation_address',
                                                        message: 'Please enter your property address' });
                                                    (!checkedValues.selling && !checkedValues.letting) && setError('selling_reason', { type: 'required',
                                                        message: 'Please select a selling reason' });
                                                } else {
                                                    setCurrentStep(2);
                                                }
                                            }}
                                            renderIcon={<ArrowRightIcon />}
                                            type='button'>
                                            Proceed
                                        </Button>
                                    </div>
                                </>
                            )}

                            {/* Step 02 fields */}
                            {currentStep === 2 && (
                                <>
                                    <div className='flex flex-wrap mxn5 mb7'>
                                        {/* Email */}
                                        <div className='col-12 px5 '>
                                            <div className='flex width-100 items-center'>
                                                <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                                Email
                                                </span>
                                                <input
                                                    {...register('email', { required: 'Please enter your email.' })}
                                                    className={`border-top border-left border-right width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-3 pl5 md-pl6${errors?.['firstname']?.message ? '' : ' border-bottom'}${errors?.['email']?.message ? ' border red' : ' black'}`}
                                                    id='frm-email'/>
                                            </div>
                                            {errors?.['email']?.message && (
                                                <p className='my5 red width-100'>
                                                    {errors?.['email']?.message}
                                                </p>
                                            )}
                                        </div>

                                        {/* First name */}
                                        <div className='col-12 px5 '>
                                            <div className='flex width-100 items-center'>
                                                <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                                First name
                                                </span>
                                                <input
                                                    {...register('firstname', { required: 'Please enter your first name.' })}
                                                    className={`border-left border-right width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-3 pl5 md-pl6${errors?.['email']?.message ? ' border-top' : ''}${errors?.['lastname']?.message ? '' : ' border-bottom'}${errors?.['firstname']?.message ? ' border red' : ' black'}`}
                                                    id='frm-firstname'/>
                                            </div>
                                            {errors?.['firstname']?.message && (
                                                <p className='my5 red width-100'>
                                                    {errors?.['firstname']?.message}
                                                </p>
                                            )}
                                        </div>

                                        {/* Last name */}
                                        <div className='col-12 px5 '>
                                            <div className='flex width-100 items-center'>
                                                <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                                Last name
                                                </span>
                                                <input
                                                    {...register('lastname', { required: 'Please enter your last name.' })}
                                                    className={`border-left border-right width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-3 pl5 md-pl6${errors?.['firstname']?.message ? ' border-top' : ''}${errors?.['phone']?.message ? '' : ' border-bottom'}${errors?.['lastname']?.message ? ' border red' : ' black'}`}
                                                    id='frm-lastname'/>
                                            </div>
                                            {errors?.['lastname']?.message && (
                                                <p className='my5 red width-100'>
                                                    {errors?.['lastname']?.message}
                                                </p>
                                            )}
                                        </div>

                                        {/* Phone number */}
                                        <div className='col-12 px5 '>
                                            <div className='flex width-100 items-center'>
                                                <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                                Phone Number
                                                </span>
                                                <input
                                                    {...register('phone', { required: 'Please enter your phone number.' })}
                                                    className={`number-input border-left border-right width-100 pr6 pt7 pb6 md-pt8 md-pb7 type-style-3 pl5 md-pl6${errors?.['lastname']?.message ? ' border-top' : ''}${errors?.['address']?.message ? '' : ' border-bottom'}${errors?.['phone']?.message ? ' border red' : ' black'}`}
                                                    id='frm-phone'
                                                    type='number'/>
                                            </div>
                                            {errors?.['phone']?.message && (
                                                <p className='my5 red width-100'>
                                                    {errors?.['phone']?.message}
                                                </p>
                                            )}
                                        </div>

                                        {/* Current address */}
                                        <div className='col-12 px5 '>
                                            <div className='flex width-100 items-center relative'>
                                                <span className='absolute pl5 md-pl6 pb7 md-pb8 top-0 pt2 mt5 pointer-events-none'>
                                                Current Address
                                                </span>
                                                <textarea
                                                    {...rest}
                                                    className={`border-left border-right border-bottom width-100 type-style-3 pt7 pb6 md-pt8 md-pb7 pr6 pl5 md-pl6${errors?.['phone']?.message ? ' border-top' : ''}${errors?.['address']?.message ? ' border-top red' : ' black'}`}
                                                    id='frm-address'
                                                    name='address'
                                                    onChange={() => handleInputHeight()}
                                                    ref={(e) => {
                                                        ref(e);
                                                        textareaRef.current = e;
                                                    }}
                                                    rows={1}
                                                    style={{ height: height ? height + 'px' : 'auto'}}/>
                                            </div>
                                            {errors?.['address']?.message && (
                                                <p className='my5 red width-100'>
                                                    {errors?.['address']?.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Submit */}
                                    <div>
                                        <Button
                                            aria-label='Submit'
                                            as='button'
                                            className = 'bg-black white type-style-5'
                                            disabled={isLoading}
                                            look = 'fill'
                                            renderIcon={<ArrowRightIcon />}
                                            type='submit'>
                                            Book Valuation
                                        </Button>
                                    </div>
                                </>
                            )}
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default BookValuationModal;
